// QA page block: the verdict list for factory builds. Same data and same
// endpoint as the Build tab column — QA works here because the Build tab
// itself (keystores, SDK keys) is developer-only.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Select } from '@/components/Select';
import { BuildQaControl } from '@/components/BuildQaControl';
import { getBuildQa, BUILD_QA_LABELS, type BuildQaStatus } from '@/api/build-qa';

const FILTERS: { value: string; label: string }[] = [
  { value: 'all', label: 'Все билды' },
  { value: 'unverified', label: BUILD_QA_LABELS.unverified },
  { value: 'passed', label: BUILD_QA_LABELS.passed },
  { value: 'failed', label: BUILD_QA_LABELS.failed },
];

function fmt(unix: number): string {
  return new Date(unix * 1000).toLocaleString();
}

export function BuildVerification() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('all');
  const qaStatus = filter === 'all' ? undefined : (filter as BuildQaStatus);

  const { data, isLoading } = useQuery({
    queryKey: ['build-qa', filter],
    queryFn: () => getBuildQa({ qa_status: qaStatus, limit: 50 }),
  });

  const summary = data?.summary;
  const builds = data?.builds ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ['build-qa'] });

  return (
    <div className="bg-surface border border-border rounded-lg mt-6">
      <div className="px-6 py-4 border-b border-border">
        <h2 className="text-lg font-medium text-content">Проверка билдов</h2>
        <p className="text-sm text-content-subtle mt-1">
          Статус тестирования каждого APK/AAB и описание проверки. Например:
          «Прошел проверку, но не работал Google AdMob». Менять могут QA и разработчики.
        </p>
      </div>

      <div className="px-6 py-4 border-b border-border flex flex-wrap items-center gap-4">
        <div className="w-56">
          <Select value={filter} options={FILTERS} onChange={setFilter} size="sm" />
        </div>
        {summary && (
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="text-content-subtle">Всего: <span className="text-content">{summary.total}</span></span>
            <span className="text-content-subtle">{BUILD_QA_LABELS.unverified}: <span className="text-content">{summary.unverified}</span></span>
            <span className="text-content-subtle">{BUILD_QA_LABELS.passed}: <span className="text-green-400">{summary.passed}</span></span>
            <span className="text-content-subtle">{BUILD_QA_LABELS.failed}: <span className="text-danger">{summary.failed}</span></span>
          </div>
        )}
      </div>

      {isLoading ? (
        <p className="px-6 py-5 text-sm text-content-subtle">Загрузка…</p>
      ) : builds.length === 0 ? (
        <p className="px-6 py-5 text-sm text-content-subtle">Билдов нет.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-content-subtle border-b border-border">
                <th className="px-6 py-2 font-medium">Приложение</th>
                <th className="px-6 py-2 font-medium">Версия</th>
                <th className="px-6 py-2 font-medium">Сборка</th>
                <th className="px-6 py-2 font-medium">Дата</th>
                <th className="px-6 py-2 font-medium">Проверка</th>
              </tr>
            </thead>
            <tbody>
              {builds.map((b) => (
                <tr key={b.id} className="border-b border-border last:border-0 align-top">
                  <td className="px-6 py-3">
                    <div className="text-content">{b.app_name || b.app_id}</div>
                    {b.bundle_id && <div className="text-xs text-content-subtle font-mono">{b.bundle_id}</div>}
                  </td>
                  <td className="px-6 py-3 text-content-muted whitespace-nowrap">
                    <span className="uppercase">{b.build_type}</span> {b.version_name} ({b.version_code})
                  </td>
                  <td className="px-6 py-3 text-content-muted" title={b.error || undefined}>{b.status}</td>
                  <td className="px-6 py-3 text-content-subtle whitespace-nowrap">{fmt(b.created_at)}</td>
                  <td className="px-6 py-3">
                    <BuildQaControl
                      buildId={b.id}
                      status={b.qa_status}
                      note={b.qa_note}
                      reviewer={b.qa_by_name || b.qa_by_email}
                      reviewedAt={b.qa_at}
                      onSaved={refresh}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
